import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, X as XIcon } from "lucide-react";
import { usd } from "@/lib/pricing";

export const Route = createFileRoute("/admin/cars")({ component: AdminCars });

type Car = any;
const EMPTY: Car = {
  make: "", model: "", year: new Date().getFullYear(), trim: "", description: "",
  photos: [], daily_rate: 100, weekly_discount_pct: 10, monthly_discount_pct: 20,
  security_deposit: 500, location: "", seats: 5, transmission: "Automatic", fuel_type: "Gasoline",
  features: [], active: true, is_featured: false,
};

function AdminCars() {
  const [cars, setCars] = useState<Car[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<Car | null>(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from("cars").select("*").order("created_at", { ascending: false });
    setCars(data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const remove = async (id: string) => {
    if (!confirm("Delete this car? Existing bookings will keep their reference.")) return;
    const { error } = await supabase.from("cars").delete().eq("id", id);
    if (error) { toast.error(error.message); return; }
    toast.success("Deleted"); load();
  };

  const openNew = () => { setEditing({ ...EMPTY }); setOpen(true); };
  const openEdit = (c: Car) => { setEditing({ ...c }); setOpen(true); };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h1 className="font-display text-3xl text-espresso">Cars</h1>
        <Button onClick={openNew} className="bg-primary"><Plus className="mr-1 h-4 w-4" /> Add car</Button>
      </div>

      {loading ? <p className="mt-6 text-muted-foreground">Loading…</p> :
        cars.length === 0 ? <Card className="mt-6 p-8 text-center text-muted-foreground">No cars yet. Add your first vehicle.</Card> :
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cars.map(c => (
            <Card key={c.id} className="overflow-hidden">
              <div className="aspect-[4/3] w-full bg-muted">
                {c.photos?.[0] ? <img src={c.photos[0]} alt="" className="h-full w-full object-cover" /> : <div className="grid h-full place-items-center text-muted-foreground text-sm">No image</div>}
              </div>
              <div className="p-4">
                <div className="flex justify-between">
                  <div>
                    <div className="font-display text-lg text-espresso">{c.year} {c.make} {c.model}</div>
                    <div className="text-sm text-muted-foreground">{usd(Number(c.daily_rate))} / day</div>
                  </div>
                  <span className={`rounded px-2 py-0.5 text-xs ${c.active ? "bg-accent/20 text-accent" : "bg-muted text-muted-foreground"}`}>{c.active ? "Active" : "Hidden"}</span>
                </div>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="outline" onClick={() => openEdit(c)}><Pencil className="mr-1 h-3 w-3" /> Edit</Button>
                  <Button size="sm" variant="destructive" onClick={() => remove(c.id)}><Trash2 className="h-3 w-3" /></Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      }

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing?.id ? "Edit car" : "Add car"}</DialogTitle></DialogHeader>
          {editing && <CarForm car={editing} onSave={() => { setOpen(false); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CarForm({ car, onSave }: { car: Car; onSave: () => void }) {
  const [f, setF] = useState<Car>(car);
  const [uploading, setUploading] = useState(false);
  const set = (k: string, v: any) => setF((p: Car) => ({ ...p, [k]: v }));

  const upload = async (file: File) => {
    setUploading(true);
    const path = `cars/${Date.now()}-${file.name}`;
    const { error } = await supabase.storage.from("car-photos").upload(path, file);
    if (error) { toast.error(error.message); setUploading(false); return; }
    const { data: { publicUrl } } = supabase.storage.from("car-photos").getPublicUrl(path);
    set("photos", [...(f.photos || []), publicUrl]);
    setUploading(false);
  };

  const save = async () => {
    const payload = {
      make: f.make, model: f.model, year: Number(f.year), trim: f.trim || null,
      description: f.description || null, photos: f.photos || [],
      daily_rate: Number(f.daily_rate), weekly_discount_pct: Number(f.weekly_discount_pct),
      monthly_discount_pct: Number(f.monthly_discount_pct), security_deposit: Number(f.security_deposit),
      location: f.location || null, seats: Number(f.seats) || null, transmission: f.transmission || null,
      fuel_type: f.fuel_type || null, body_type: f.body_type || null, features: f.features || [], active: f.active,
      is_featured: !!f.is_featured,
    };
    const res = f.id
      ? await supabase.from("cars").update(payload).eq("id", f.id)
      : await supabase.from("cars").insert(payload);
    if (res.error) { toast.error(res.error.message); return; }
    toast.success("Saved"); onSave();
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <F label="Make"><Input value={f.make} onChange={e => set("make", e.target.value)} /></F>
        <F label="Model"><Input value={f.model} onChange={e => set("model", e.target.value)} /></F>
        <F label="Year"><Input type="number" value={f.year} onChange={e => set("year", e.target.value)} /></F>
      </div>
      <F label="Trim (optional)"><Input value={f.trim || ""} onChange={e => set("trim", e.target.value)} /></F>
      <F label="Description"><Textarea value={f.description || ""} onChange={e => set("description", e.target.value)} rows={3} /></F>

      <div className="grid grid-cols-2 gap-3">
        <F label="Daily rate ($)"><Input type="number" step="0.01" value={f.daily_rate} onChange={e => set("daily_rate", e.target.value)} /></F>
        <F label="Security deposit ($)"><Input type="number" step="0.01" value={f.security_deposit} onChange={e => set("security_deposit", e.target.value)} /></F>
        <F label="Weekly discount (%)"><Input type="number" step="0.1" value={f.weekly_discount_pct} onChange={e => set("weekly_discount_pct", e.target.value)} /></F>
        <F label="Monthly discount (%)"><Input type="number" step="0.1" value={f.monthly_discount_pct} onChange={e => set("monthly_discount_pct", e.target.value)} /></F>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <F label="Pickup location"><Input value={f.location || ""} onChange={e => set("location", e.target.value)} /></F>
        <F label="Seats"><Input type="number" value={f.seats || ""} onChange={e => set("seats", e.target.value)} /></F>
        <F label="Transmission"><Input value={f.transmission || ""} onChange={e => set("transmission", e.target.value)} /></F>
        <F label="Fuel type"><Input value={f.fuel_type || ""} onChange={e => set("fuel_type", e.target.value)} /></F>
        <F label="Body type">
          <select
            value={f.body_type || ""}
            onChange={e => set("body_type", e.target.value)}
            className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
          >
            <option value="">— Select —</option>
            <option value="exotic">Exotic</option>
            <option value="suv">SUV</option>
            <option value="convertible">Convertible</option>
            <option value="sedan">Sedan</option>
            <option value="coupe">Coupe</option>
            <option value="truck">Truck</option>
            <option value="van">Van</option>
          </select>
        </F>
      </div>
      <F label="Features (comma-separated)">
        <Input value={(f.features || []).join(", ")} onChange={e => set("features", e.target.value.split(",").map(s => s.trim()).filter(Boolean))} />
      </F>

      <div>
        <Label>Photos</Label>
        <div className="mt-2 grid grid-cols-4 gap-2">
          {(f.photos || []).map((p: string, i: number) => (
            <div key={i} className="group relative aspect-square">
              <img src={p} className="h-full w-full rounded object-cover" />
              <button onClick={() => set("photos", f.photos.filter((_: any, j: number) => j !== i))} className="absolute right-1 top-1 rounded-full bg-destructive p-1 text-destructive-foreground opacity-0 group-hover:opacity-100">
                <XIcon className="h-3 w-3" />
              </button>
            </div>
          ))}
          <label className="grid aspect-square cursor-pointer place-items-center rounded border-2 border-dashed border-border text-muted-foreground hover:border-accent hover:text-accent">
            <Upload className="h-5 w-5" />
            <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={e => { const file = e.target.files?.[0]; if (file) upload(file); }} />
          </label>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Switch checked={f.active} onCheckedChange={v => set("active", v)} />
        <Label>Active (visible to customers)</Label>
      </div>

      <div className="flex items-center gap-2">
        <Switch checked={!!f.is_featured} onCheckedChange={v => set("is_featured", v)} />
        <Label>Featured (show on homepage, rank first on fleet)</Label>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button onClick={save} className="bg-primary">Save car</Button>
      </div>
    </div>
  );
}

function F({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-1"><Label>{label}</Label>{children}</div>;
}
